/**
 * VibeCheck CLI v2 — Project Detection
 * Auto-detects framework, language, package manager, and project structure.
 */

import * as fs from 'fs';
import * as path from 'path';
import type { ProjectInfo } from '../types';

export function detectProject(rootPath: string): ProjectInfo {
  const pkgPath = path.join(rootPath, 'package.json');
  let pkg: Record<string, any> = {};
  try { pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf-8')); } catch { /* no package.json */ }

  const deps = { ...((pkg.dependencies || {}) as Record<string, string>), ...((pkg.devDependencies || {}) as Record<string, string>) };
  const depNames = Object.keys(deps);

  const framework = detectFramework(depNames, rootPath);
  const language = detectLanguage(rootPath);
  const packageManager = detectPackageManager(rootPath);

  return {
    name: pkg.name || path.basename(rootPath),
    framework,
    language,
    packageManager,
    hasTypeScript: language === 'TypeScript',
    hasTests: depNames.some(d => ['vitest', 'jest', 'mocha', 'ava', 'tap', '@testing-library/react', 'cypress', 'playwright'].includes(d)),
    hasApi: fs.existsSync(path.join(rootPath, 'src', 'api')) || fs.existsSync(path.join(rootPath, 'app', 'api')) || fs.existsSync(path.join(rootPath, 'pages', 'api')) || fs.existsSync(path.join(rootPath, 'routes')),
    hasAuth: depNames.some(d => ['next-auth', 'passport', 'jsonwebtoken', 'clerk', '@clerk/nextjs', 'auth0', '@auth0/nextjs-auth0', 'lucia', 'better-auth', '@supabase/auth-helpers-nextjs'].includes(d)),
    hasDocs: fs.existsSync(path.join(rootPath, 'docs')) || fs.existsSync(path.join(rootPath, 'README.md')),
    dependencies: Object.keys(pkg.dependencies || {}),
    devDependencies: Object.keys(pkg.devDependencies || {}),
    fileCount: 0, // filled by caller if needed
    rootPath,
  };
}

function detectFramework(deps: string[], rootPath: string): string {
  // Next.js
  if (deps.includes('next')) {
    const hasAppDir = fs.existsSync(path.join(rootPath, 'app'));
    return hasAppDir ? 'Next.js (App Router)' : 'Next.js (Pages Router)';
  }
  // Nuxt
  if (deps.includes('nuxt') || deps.includes('nuxt3')) return 'Nuxt.js';
  // SvelteKit
  if (deps.includes('@sveltejs/kit')) return 'SvelteKit';
  // Remix
  if (deps.includes('@remix-run/react')) return 'Remix';
  // Astro
  if (deps.includes('astro')) return 'Astro';
  // Vite + React
  if (deps.includes('vite') && deps.includes('react')) return 'React (Vite)';
  // CRA
  if (deps.includes('react-scripts')) return 'React (CRA)';
  // React
  if (deps.includes('react')) return 'React';
  // Vue
  if (deps.includes('vue')) return 'Vue.js';
  // Svelte
  if (deps.includes('svelte')) return 'Svelte';
  // Angular
  if (deps.includes('@angular/core')) return 'Angular';
  // Express
  if (deps.includes('express')) return 'Express';
  // Fastify
  if (deps.includes('fastify')) return 'Fastify';
  // Hono
  if (deps.includes('hono')) return 'Hono';
  // NestJS
  if (deps.includes('@nestjs/core')) return 'NestJS';
  // Django/Flask/FastAPI (check for Python)
  if (fs.existsSync(path.join(rootPath, 'pyproject.toml')) || fs.existsSync(path.join(rootPath, 'requirements.txt'))) {
    try {
      const reqs = fs.readFileSync(path.join(rootPath, 'requirements.txt'), 'utf-8');
      if (reqs.includes('django')) return 'Django';
      if (reqs.includes('fastapi')) return 'FastAPI';
      if (reqs.includes('flask')) return 'Flask';
    } catch { /* ignore */ }
    return 'Python';
  }
  // Go
  if (fs.existsSync(path.join(rootPath, 'go.mod'))) return 'Go';
  // Rust
  if (fs.existsSync(path.join(rootPath, 'Cargo.toml'))) return 'Rust';
  // Node.js generic
  if (deps.length > 0) return 'Node.js';
  return 'Unknown';
}

function detectLanguage(rootPath: string): string {
  if (fs.existsSync(path.join(rootPath, 'tsconfig.json'))) return 'TypeScript';
  if (fs.existsSync(path.join(rootPath, 'jsconfig.json'))) return 'JavaScript';
  if (fs.existsSync(path.join(rootPath, 'pyproject.toml')) || fs.existsSync(path.join(rootPath, 'requirements.txt'))) return 'Python';
  if (fs.existsSync(path.join(rootPath, 'go.mod'))) return 'Go';
  if (fs.existsSync(path.join(rootPath, 'Cargo.toml'))) return 'Rust';
  if (fs.existsSync(path.join(rootPath, 'package.json'))) return 'JavaScript';
  return 'Unknown';
}

function detectPackageManager(rootPath: string): string {
  if (fs.existsSync(path.join(rootPath, 'bun.lockb'))) return 'bun';
  if (fs.existsSync(path.join(rootPath, 'pnpm-lock.yaml'))) return 'pnpm';
  if (fs.existsSync(path.join(rootPath, 'yarn.lock'))) return 'yarn';
  if (fs.existsSync(path.join(rootPath, 'package-lock.json'))) return 'npm';
  return 'npm';
}
